import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, MessageCircle } from 'lucide-react';
import { fetchFaqs } from '@/lib/api';
import SEO from '@/components/SEO';

function FaqAnswer({ text }) {
  // Render answer with simple markdown: lines starting with "- " become bullets, blank lines = paragraph breaks
  const lines = (text || '').split('\n');
  const blocks = [];
  let buffer = [];
  let listBuffer = [];

  const flushBuffer = () => {
    if (buffer.length) {
      blocks.push({ type: 'p', text: buffer.join(' ') });
      buffer = [];
    }
  };
  const flushList = () => {
    if (listBuffer.length) {
      blocks.push({ type: 'ul', items: listBuffer });
      listBuffer = [];
    }
  };

  lines.forEach(raw => {
    const line = raw.trim();
    if (line.startsWith('- ') || line.startsWith('• ')) {
      flushBuffer();
      listBuffer.push(line.replace(/^[-•]\s*/, ''));
    } else if (line === '') {
      flushBuffer();
      flushList();
    } else {
      flushList();
      buffer.push(line);
    }
  });
  flushBuffer();
  flushList();

  return (
    <div className="space-y-2 text-gray-600 text-sm sm:text-base leading-relaxed">
      {blocks.map((b, i) =>
        b.type === 'ul' ? (
          <ul key={i} className="list-disc list-inside space-y-1">
            {b.items.map((it, j) => <li key={j}>{it}</li>)}
          </ul>
        ) : (
          <p key={i}>{b.text}</p>
        )
      )}
    </div>
  );
}

function FaqItem({ q, a, isOpen, onToggle, index }) {
  return (
    <div
      data-testid={`faq-item-${index}`}
      className="border border-gray-200 rounded-xl bg-white hover:border-[#F26522]/30 transition-colors overflow-hidden"
    >
      <button
        type="button"
        onClick={onToggle}
        data-testid={`faq-toggle-${index}`}
        aria-expanded={isOpen}
        className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left"
      >
        <span className="text-[#0B1D3A] font-semibold text-base sm:text-lg">{q}</span>
        <ChevronDown size={20} className={`shrink-0 text-[#F26522] transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      <div className={`grid transition-all duration-300 ${isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}>
        <div className="overflow-hidden">
          <div className="px-6 pb-5">
            <FaqAnswer text={a} />
          </div>
        </div>
      </div>
    </div>
  );
}

export default function FAQ() {
  const [faqs, setFaqs] = useState([]);
  const [openIndex, setOpenIndex] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFaqs()
      .then(data => setFaqs(data || []))
      .catch(() => setFaqs([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="faq-page" className="pt-[68px]">
      <SEO
        title="Frequently Asked Questions"
        description="Answers to common questions about cage bookings, lanes, packages, bowling machines, and more at Propel Sports NC."
        path="/faq"
      />

      <section className="relative py-24 bg-[#0B1D3A] overflow-hidden">
        <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: 'radial-gradient(ellipse 600px 400px at 30% 40%, rgba(242,101,34,0.25) 0%, transparent 70%)'
        }} />
        <div className="relative z-10 max-w-7xl mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-4">Frequently Asked Questions</h1>
          <p className="text-gray-300 text-lg max-w-2xl mx-auto">Everything you need to know about bookings, lanes, packages and more.</p>
        </div>
      </section>

      <section className="py-20 bg-gray-50">
        <div className="max-w-3xl mx-auto px-4 space-y-4" data-testid="faq-list">
          {loading && <p className="text-center text-gray-400 text-sm">Loading...</p>}
          {!loading && faqs.length === 0 && (
            <p className="text-center text-gray-400 text-sm">No FAQs yet. Check back soon.</p>
          )}
          {!loading && faqs.map((item, i) => (
            <FaqItem
              key={item.id}
              q={item.question}
              a={item.answer}
              index={i}
              isOpen={openIndex === i}
              onToggle={() => setOpenIndex(openIndex === i ? -1 : i)}
            />
          ))}
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-2xl font-bold text-[#0B1D3A] mb-3">Still have questions?</h2>
          <p className="text-gray-500 mb-6">Reach out to our team — we usually respond within a few hours.</p>
          <Link
            to="/contact"
            data-testid="faq-contact-cta"
            className="inline-flex items-center gap-2 bg-[#F26522] hover:bg-[#D9551A] text-white px-8 py-3 rounded-lg font-semibold text-sm transition-all hover:shadow-lg hover:shadow-orange-500/20"
          >
            <MessageCircle size={16} />
            Contact Us
          </Link>
        </div>
      </section>
    </div>
  );
}
